import type { ReactNode, KeyboardEvent } from 'react';
import { cn } from '@/lib/cn';

/**
 * One flat list row — the de-boxing primitive. Sits on a `divide-y divide-border`
 * <ul>, so rows are separated by hairlines instead of nested bordered cards. An
 * icon slot, a title with an optional muted meta line (the "why / who"), and a
 * right-aligned action/affordance slot. Clickable when `onClick` is set.
 */
export function ListRow({
  icon,
  title,
  meta,
  actions,
  onClick,
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  const interactive = !!onClick;
  return (
    <li
      onClick={onClick}
      {...(interactive
        ? {
            role: 'button',
            tabIndex: 0,
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick!();
              }
            },
          }
        : {})}
      className={cn(
        'group flex items-center gap-3 py-3',
        interactive &&
          'cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        className,
      )}
    >
      {icon && (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-2 text-fg-muted">
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'truncate text-sm font-medium text-fg',
              interactive && 'group-hover:text-primary',
            )}
          >
            {title}
          </span>
        </div>
        {meta && <div className="mt-0.5 truncate text-xs text-fg-subtle">{meta}</div>}
      </div>
      {actions && (
        <div
          className="flex shrink-0 items-center gap-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          {actions}
        </div>
      )}
    </li>
  );
}
